import { type FlexProps } from '@ironhack/design-system2/components/flex';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms/structured-text';

import { getPdpOptions } from '@/app/urls/get-course-campus-options';
import { type BasePageParams } from '@/app/urls/url-mapping';
import { type DatoBlock, type DatoButtonBlock } from '@/lib/datocms/types';
import { urlToDatoLocale } from '@/lib/utils';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { type NameValidationMessages } from '@/lib/utils/form-validations/schemas/name-schema';
import { type GaPageData } from '@/types';
import { HeroApplyForm as HeroApplyFormClient } from './hero-apply-form.client';

export type HeroApplyForm = DatoBlock<
  {
    buttonText: string;
    campusLabel: string;
    campusPlaceholder: string;
    campusRequiredMessage: string;
    cohortLabel: string;
    cohortPlaceholder: string;
    cohortRequiredMessage: string;
    completeFields: string;
    courseLabel: string;
    coursePlaceholder: string;
    courseRequiredMessage: string;
    emailLabel: string;
    emailPlaceholder: string;
    emailValidationMessages: ValidationMessageSet;
    errorMessage: string;
    firstNameLabel: string;
    firstNamePlaceholder: string;
    firstNameValidationMessages: NameValidationMessages;
    formatLabel: string;
    formatPlaceholder: string;
    formatRequiredMessage: string;
    formOpenButtonText: string;
    lastNameLabel: string;
    lastNamePlaceholder: string;
    lastNameValidationMessages: NameValidationMessages;
    legalText: StructuredTextGraphQlResponse;
    nextStepButtonText: string;
    noCohortsErrorMessage: string;
    phoneInvalidMessage: string;
    phoneLabel: string;
    phonePlaceholder: string;
    phoneRequiredMessage: string;
    step1: string;
    step2: string;
    step3: string;
    step4Title: string;
    thankYouButton: [DatoButtonBlock];
    thankYouText: string;
    thankYouTitle: string;
    title: string;
  },
  'HeroApplyFormRecord'
>;

type Props = {
  applyForm: HeroApplyForm;
  params: BasePageParams;
  gaPageData: GaPageData;
} & FlexProps;

export const HeroApplyForm = (props: Props): ReactElement => {
  const { applyForm, gaPageData, params, ...flexProps } = props;
  const { region, language } = params;

  const pdpOptions = getPdpOptions(region, language);
  const locale = urlToDatoLocale[`${region}${language}`];
  return (
    <HeroApplyFormClient
      applyForm={applyForm}
      campuses={pdpOptions.campuses}
      courses={pdpOptions.courses}
      gaPageData={gaPageData}
      locale={locale}
      params={params}
      {...flexProps}
    />
  );
};
